import { CalculatorBackButton, CalculatorCurrencyField, CalculatorSubmitButton } from 'components/common';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { scrollToTop } from 'utils/general';
import { saveAffordabilityFormToLocalStorage, setLastAffordabilityCalcPage } from 'utils/localStorage';
import { CalculatorLayout } from '../CalculatorLayout';
import { AffordabilityCalculatorForm, CalculatorPages } from '../types';
import { OutgoingsQuestions } from './questions';

export const OutgoingsPage = () => {
  const intl = useIntl();
  const form = useFormContext();
  const formFields = form.watch();
  const [searchParams, setSearchParams] = useSearchParams();
  const endAdornment = intl.formatMessage({ id: 'common.monthly' }).toLocaleLowerCase();
  const { creditCommitments, expenditure } = OutgoingsQuestions;

  const goToNextPage = async () => {
    const triggerResultPromise = form.trigger(`outgoings` as keyof AffordabilityCalculatorForm, {
      shouldFocus: true,
    });
    const isNoErrors = await triggerResultPromise;

    if (isNoErrors) {
      saveAffordabilityFormToLocalStorage(formFields);
      setLastAffordabilityCalcPage(CalculatorPages.reviewAnswers);
      searchParams.set('section', CalculatorPages.reviewAnswers);
      setSearchParams(searchParams);
      scrollToTop();
    }
  };

  const goToPreviousPage = () => {
    searchParams.set('section', CalculatorPages.applicantDetails);
    setSearchParams(searchParams);
    scrollToTop();
  };

  return (
    <CalculatorLayout currentSectionNumber={3} additionalClasses={'after:w-3/5'}>
      <h2 className={'text-dark font-satoshiBlack xs:text-xl relative font-black md:mt-16 lg:text-2xl'}>
        <FormattedMessage id={creditCommitments.titleKey} />
      </h2>
      <br />
      <CalculatorCurrencyField
        name={`outgoings.creditCommitments.unsecuredLoanRepayments`}
        titleKey={creditCommitments.unsecuredLoanRepayments.titleKey}
        textKey={creditCommitments.unsecuredLoanRepayments.textKey}
        textFieldProps={{ InputProps: { endAdornment } }}
        className={'xs:mb-5 md:mb-10'}
      />
      <CalculatorCurrencyField
        name={`outgoings.creditCommitments.securedLoanRepayments`}
        titleKey={creditCommitments.securedLoanRepayments.titleKey}
        textKey={creditCommitments.securedLoanRepayments.textKey}
        textFieldProps={{ InputProps: { endAdornment } }}
        className={'xs:mb-5 md:mb-10'}
      />
      <CalculatorCurrencyField
        name={`outgoings.creditCommitments.creditStoreCardsBalance`}
        titleKey={creditCommitments.creditStoreCardsBalance.titleKey}
        textKey={creditCommitments.creditStoreCardsBalance.textKey}
        className={'xs:mb-5 md:mb-10'}
      />
      <CalculatorCurrencyField
        name={`outgoings.expenditure.maintenance`}
        titleKey={expenditure.maintenance.titleKey}
        textKey={expenditure.maintenance.textKey}
        periodProps={{ options: expenditure.periodOptions }}
        className={'xs:mb-6 md:mb-12'}
        isPeriod
      />
      <hr className={'border-fog xs:mb-6 border border-t-0 border-solid md:mb-12'} />

      <h2
        className={'text-dark font-satoshiBlack xs:text-xl xs:mb-5 relative font-black md:mb-10 md:mt-16 lg:text-2xl'}
      >
        <FormattedMessage id={expenditure.titleKey} />
      </h2>
      <CalculatorCurrencyField
        name={`outgoings.expenditure.childcare`}
        titleKey={expenditure.childcare.titleKey}
        textKey={expenditure.childcare.textKey}
        periodProps={{ options: expenditure.periodOptions }}
        className={'xs:mb-5 md:mb-10'}
        isPeriod
      />
      <CalculatorCurrencyField
        name={`outgoings.expenditure.travelExpenses`}
        titleKey={expenditure.travelExpenses.titleKey}
        textKey={expenditure.travelExpenses.textKey}
        periodProps={{ options: expenditure.periodOptions }}
        className={'xs:mb-5 md:mb-10'}
        isPeriod
      />
      <div className={'xs:my-10 xs:flex-col-reverse flex w-full justify-between md:flex-row'}>
        <CalculatorBackButton onClick={goToPreviousPage} />
        <div className={'h-8 w-full md:hidden'}></div>
        <CalculatorSubmitButton onClick={goToNextPage} />
      </div>
    </CalculatorLayout>
  );
};
